import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';

export interface CaseConfig {
  tenantId: string;
  groupCriteria: 'portfolio' | 'single_operation' | 'custom_field' | string;
  configParams: string[];
}

@Injectable({ providedIn: 'root' })
export class CaseConfigService {
  public caseConfig!: CaseConfig;
  private loaded = false;
  private readonly CACHE_KEY = 'case_config';

  constructor(private apollo: Apollo) {}

  loadCaseConfig(tenantId: string): Promise<boolean> {
    if (this.loaded) {
      return Promise.resolve(true);
    }

    if (this.loadFromCache(tenantId)) {
      return Promise.resolve(true);
    }

    const query = gql`
      query ($tenantId: ID!) {
        caseConfig(tenantId: $tenantId) {
          tenantId
          groupCriteria
          configParams
        }
      }
    `;

    return this.apollo
      .query<{ caseConfig: CaseConfig }>({
        query,
        variables: { tenantId },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })
      .toPromise()
      .then((res) => {
        if (res?.data?.caseConfig) {
          this.caseConfig = res.data.caseConfig;
          this.loaded = true;
          localStorage.setItem(this.CACHE_KEY, JSON.stringify(this.caseConfig));
          return true;
        } else {
          console.warn('caseConfig is missing from response:', res);
          return this.loadFromCache(tenantId);
        }
      })
      .catch((err) => {
        console.error('Failed to load caseConfig:', err);
        return this.loadFromCache(tenantId);
      });
  }

  getCaseConfig(): CaseConfig | null {
    if (this.loaded) return this.caseConfig;

    const cached = localStorage.getItem(this.CACHE_KEY);
    if (!cached) return null;

    try {
      const parsed = JSON.parse(cached);
      this.caseConfig = parsed;
      this.loaded = true;
      return parsed;
    } catch (e) {
      console.error('Invalid cached caseConfig:', e);
      return null;
    }
  }

  private loadFromCache(expectedTenantId: string): boolean {
    const cachedConfig = localStorage.getItem(this.CACHE_KEY);
    if (!cachedConfig) return false;

    try {
      const parsed = JSON.parse(cachedConfig);
      if (parsed.tenantId !== expectedTenantId) {
        console.warn('Cached caseConfig belongs to another tenant');
        return false;
      }

      this.caseConfig = parsed;
      this.loaded = true;
      return true;
    } catch (e) {
      console.error('Invalid cached caseConfig:', e);
      return false;
    }
  }
}
